import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Crown, ChevronDown, X } from "lucide-react";
import { useAuth } from "../../../context/AuthContext";
import { useTheme } from "../../../context/ThemeContext";
import toast from "react-hot-toast";

const UpgradePage = () => {
  const navigate = useNavigate();
  const { user, upgradePlan } = useAuth();
  const { isDarkTheme } = useTheme();
  const [billingFrequency, setBillingFrequency] = useState("monthly");
  const [selectedPlan, setSelectedPlan] = useState("premium");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const dropdownRef = useRef(null);

    const plans = {
        basic: {
            name: "Basic",
            monthlyPrice: "Free for 14 days, then $6.99 ",
            annualPrice: "$67.99",
            features: [
                "Up to 1,000 tokens/month",
                "Upload up to 5 PDF files",
            ],
        },
        premium: {
            name: "Premium",
            monthlyPrice: "$24.99",
            annualPrice: "$239.99 ",
            features: [
                "Up to 100K tokens/month",
                "Priority response time",
                "Upload up to 50 PDF files/month",
            ],
        },
        enterprise: {
            name: "Enterprise",
            monthlyPrice: "$99.99",
            annualPrice: "$959.99",
            features: [
                "Up to 500K tokens/month",
                "Upload PDF + Word files (unlimited)",
                "Custom AI features & integrations",
                "Web search",
                "User Customization",
                "Dedicated support & training",
            ],
        },
    };
    
  const handleUpgrade = async () => {
    setIsProcessing(true);
    try {
      navigate("/checkout", {
        state: {
          selectedPlan: plans[selectedPlan].name,
          billingFrequency: billingFrequency,
          price: getPrice(selectedPlan),
          isAnnual: billingFrequency === "annual",
          features: plans[selectedPlan].features
        }
      });
    } catch (error) {
      toast.error("Failed to upgrade plan");
      console.error("Error upgrading plan:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleContactSales = () => {
    window.open("mailto:sales@ogfleet.com?subject=Enterprise%20Plan%20Inquiry", "_blank");
    toast.success("Opening email client to contact sales");
  };

  const getPrice = (planKey) => {
    const plan = plans[planKey];
    return billingFrequency === "monthly" ? plan.monthlyPrice : plan.annualPrice;
  };

  const currentPlan = user?.plan || "basic";
  const billingOptions = [
    { value: "monthly", label: "Monthly Billing" },
    { value: "annual", label: "Annual Billing" },
  ];

  return (
    <div className={`min-h-screen ${isDarkTheme ? "bg-gray-900 text-gray-100" : "bg-gray-50 text-gray-800"}`}>
      {/* Header */}
      <div className={`px-6 py-3 border-b ${isDarkTheme ? "border-gray-700" : "border-gray-200"} flex items-center backdrop-blur-sm bg-opacity-80 ${isDarkTheme ? "bg-gray-900" : "bg-white"} sticky top-0 z-10`}>
        <Crown className="w-6 h-6 text-amber-500 mr-3" />
        <h1 className="md:text-2xl text-xl font-bold ">Upgrade Your Plan</h1>
        <button
          onClick={() => navigate("/chat")}
          className={`ml-auto md:px-4 px-3 py-2 rounded-lg ${isDarkTheme ? "bg-gray-800 hover:bg-gray-700 text-gray-300" : "bg-gray-200 hover:bg-gray-300 text-gray-700"
            } transition duration-200`}
        >
          <X className="text-amber-500"></X>
        </button>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Billing Frequency Dropdown */}
        <div className="mb-8 flex justify-end relative" ref={dropdownRef}>
          <button
            className={`flex items-center justify-between px-10 py-2 border rounded-lg shadow-sm ${isDarkTheme
              ? "bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700"
              : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
              } focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-opacity-50`}
            onClick={() => setDropdownOpen(!dropdownOpen)}
          >
            <span>{billingFrequency === "monthly" ? "Monthly Billing" : "Annual Billing"}</span>
            <ChevronDown className="ml-2 w-4 h-4" />
          </button>

          {dropdownOpen && (
            <div
              className={`absolute right-0 mt-12 w-52 rounded-lg shadow-lg z-10 ${isDarkTheme ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
                }`}
            >
              {billingOptions.map((option) => (
                <button
                  key={option.value}
                  className={`block w-full text-left px-4 py-2 ${isDarkTheme ? "hover:bg-gray-700 text-gray-300" : "hover:bg-gray-100 text-gray-700"
                    } ${billingFrequency === option.value ? "text-amber-600 font-medium" : ""}`}
                  onClick={() => {
                    setBillingFrequency(option.value);
                    setDropdownOpen(false);
                  }}
                >
                  {option.label}
                  {option.value === "annual" && (
                    <span className="ml-2 text-green-600 text-xs font-medium">Save 20%</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Plan Selection */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Basic Plan */}
          <div
            className={`border-2 rounded-xl p-6 flex flex-col h-full transition-all duration-200 ${currentPlan === "basic"
              ? isDarkTheme
                ? "border-amber-500 bg-gray-800"
                : "border-amber-500 bg-amber-50"
              : isDarkTheme
                ? "border-gray-700 hover:border-gray-600"
                : "border-gray-200 hover:border-gray-300"
              }`}
          >
            <div className="mb-4 flex justify-between items-start">
              <div>
                <h3 className={`font-semibold text-lg ${isDarkTheme ? "text-gray-100" : "text-gray-800"}`}>
                  {plans.basic.name}
                </h3>
                <div className="mt-2">
                  <span className="text-2xl font-bold">{getPrice("basic")}</span>
                  <span className={`text-sm ${isDarkTheme ? "text-gray-400" : "text-gray-500"}`}>
                    /{billingFrequency === "monthly" ? "month" : "year"}
                  </span>
                </div>
              </div>
              {currentPlan === "basic" && (
                <span
                  className={`text-xs px-3 py-1 rounded-full font-medium ${isDarkTheme ? "bg-amber-800 text-amber-200" : "bg-amber-100 text-amber-800"
                    }`}
                >
                  Current
                </span>
              )}
            </div>
            <p className={`text-sm mb-4 ${isDarkTheme ? "text-gray-400" : "text-gray-500"}`}>
              For personal usage with limited needs.
            </p>
            <ul className="space-y-3 mb-6 flex-grow">
              {plans.basic.features.map((feature, index) => (
                <li key={index} className="flex items-start">
                  <Check
                    className={`w-5 h-5 mt-0.5 mr-2 flex-shrink-0 ${isDarkTheme ? "text-gray-400" : "text-gray-500"
                      }`}
                  />
                  <span className={`text-sm ${isDarkTheme ? "text-gray-300" : "text-gray-600"}`}>{feature}</span>
                </li>
              ))}
            </ul>

            {/* Button at bottom */}
            <div className="mt-auto pt-4">
              <button
                disabled={true}
                className={`w-full py-2.5 px-4 rounded-lg text-sm font-medium cursor-not-allowed ${isDarkTheme ? "bg-gray-700 text-gray-400" : "bg-gray-100 text-gray-500"
                  }`}
              >
                Current Plan
              </button>
            </div>
          </div>

          {/* Premium Plan - Highlighted */}
          <div
            onClick={() => {
              if (currentPlan !== "premium") setSelectedPlan("premium");
            }}
            className={`border-2 rounded-xl p-6 flex flex-col h-full transition-all duration-200 relative cursor-pointer 
              ${selectedPlan === "premium"
                ? "border-amber-500 shadow-xl"
                : isDarkTheme
                  ? "border-gray-700 hover:border-gray-600"
                  : "border-gray-200 hover:border-gray-300"
              } 
              ${currentPlan === "premium" ? (isDarkTheme ? "bg-gray-800" : "bg-amber-50") : ""}
              transform ${selectedPlan === "premium" ? "scale-105" : "scale-100"}`}
          >
            <div className="absolute -top-4 left-0 right-0 flex justify-center">
              <span className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs px-4 py-1 rounded-full font-medium">
                Most Popular
              </span>
            </div>
            <div className="mb-4 flex justify-between items-start pt-2">
              <div>
                <h3 className={`font-semibold text-lg ${isDarkTheme ? "text-gray-100" : "text-gray-800"}`}>
                  {plans.premium.name}
                </h3>
                <div className="mt-2">
                  <span className="text-2xl font-bold">{getPrice("premium")}</span>
                  <span className={`text-sm ${isDarkTheme ? "text-gray-400" : "text-gray-500"}`}>
                    /{billingFrequency === "monthly" ? "month" : "year"}
                  </span>
                  {billingFrequency === "annual" && (
                    <span className="ml-2 text-green-600 text-xs font-medium">Save 20%</span>
                  )}
                </div>
              </div>
              {currentPlan === "premium" && (
                <span
                  className={`text-xs px-3 py-1 rounded-full font-medium ${isDarkTheme ? "bg-amber-800 text-amber-200" : "bg-amber-100 text-amber-800"
                    }`}
                >
                  Current
                </span>
              )}
            </div>
            <p className={`text-sm mb-4 ${isDarkTheme ? "text-gray-400" : "text-gray-500"}`}>
              Perfect for professionals and small teams.
            </p>
            <ul className="space-y-3 mb-6 flex-grow">
              {plans.premium.features.map((feature, index) => (
                <li key={index} className="flex items-start">
                  <Check
                    className={`w-5 h-5 mt-0.5 mr-2 flex-shrink-0 ${isDarkTheme ? "text-green-400" : "text-green-500"
                      }`}
                  />
                  <span className={`text-sm ${isDarkTheme ? "text-gray-300" : "text-gray-600"}`}>{feature}</span>
                </li>
              ))}
            </ul>

            {/* Button at bottom */}
            <div className="mt-auto pt-4">
              {currentPlan !== "premium" && (
                <button
                  onClick={() => setSelectedPlan("premium")}
                  className={`w-full py-2.5 px-4 rounded-lg text-sm font-medium transition duration-200 ${selectedPlan === "premium"
                    ? "bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-md hover:shadow-lg"
                    : isDarkTheme
                      ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                >
                  {selectedPlan === "premium" ? "Selected" : "Select"}
                </button>
              )}
              {currentPlan === "premium" && (
                <button
                  disabled={true}
                  className={`w-full py-2.5 px-4 rounded-lg text-sm font-medium cursor-not-allowed ${isDarkTheme ? "bg-gray-700 text-gray-400" : "bg-gray-100 text-gray-500"
                    }`}
                >
                  Current Plan
                </button>
              )}
            </div>
          </div>

          {/* Enterprise Plan */}
          <div
            onClick={() => {
              if (currentPlan !== "enterprise") setSelectedPlan("enterprise");
            }}
            className={`border-2 rounded-xl p-6 flex flex-col h-full transition-all duration-200 cursor-pointer ${selectedPlan === "enterprise"
              ? "border-amber-500 shadow-lg"
              : isDarkTheme
                ? "border-gray-700 hover:border-gray-600"
                : "border-gray-200 hover:border-gray-300"
              } ${currentPlan === "enterprise" ? (isDarkTheme ? "bg-gray-800" : "bg-amber-50") : ""}`}
          >
            <div className="mb-4 flex justify-between items-start">
              <div>
                <h3 className={`font-semibold text-lg ${isDarkTheme ? "text-gray-100" : "text-gray-800"}`}>
                  {plans.enterprise.name}
                </h3>
                <div className="mt-2">
                  <span className="text-2xl font-bold">{getPrice("enterprise")}</span>
                  <span className={`text-sm ${isDarkTheme ? "text-gray-400" : "text-gray-500"}`}>
                    /{billingFrequency === "monthly" ? "month" : "year"}
                  </span>
                  {billingFrequency === "annual" && (
                    <span className="ml-2 text-green-600 text-xs font-medium">Save 20%</span>
                  )}
                </div>
              </div>
              {currentPlan === "enterprise" && (
                <span
                  className={`text-xs px-3 py-1 rounded-full font-medium ${isDarkTheme ? "bg-amber-800 text-amber-200" : "bg-amber-100 text-amber-800"
                    }`}
                >
                  Current
                </span>
              )}
            </div>
            <p className={`text-sm mb-4 ${isDarkTheme ? "text-gray-400" : "text-gray-500"}`}>
              Advanced solutions for large organizations.
            </p>
            <ul className="space-y-3 mb-6 flex-grow">
              {plans.enterprise.features.map((feature, index) => (
                <li key={index} className="flex items-start">
                  <Check
                    className={`w-5 h-5 mt-0.5 mr-2 flex-shrink-0 ${isDarkTheme ? "text-green-400" : "text-green-500"
                      }`}
                  />
                  <span className={`text-sm ${isDarkTheme ? "text-gray-300" : "text-gray-600"}`}>{feature}</span>
                </li>
              ))}
            </ul>

            {/* Button at bottom */}
            <div className="mt-auto pt-4">
              {currentPlan !== "enterprise" && (
                <button
                  onClick={() => setSelectedPlan("enterprise")}
                  className={`w-full py-2.5 px-4 rounded-lg text-sm font-medium transition duration-200 ${selectedPlan === "enterprise"
                    ? "bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-md hover:shadow-lg"
                    : isDarkTheme
                      ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                >
                  {selectedPlan === "enterprise" ? "Selected" : "Select"}
                </button>
              )}
              {currentPlan === "enterprise" && (
                <button
                  disabled={true}
                  className={`w-full py-2.5 px-4 rounded-lg text-sm font-medium cursor-not-allowed ${isDarkTheme ? "bg-gray-700 text-gray-400" : "bg-gray-100 text-gray-500"
                    }`}
                >
                  Current Plan
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Upgrade Button */}
        <div className="flex justify-center mt-10">
          <button
            onClick={handleUpgrade}
            disabled={isProcessing || selectedPlan === currentPlan}
            className={`px-8 py-2 rounded-lg text-lg font-semibold transition duration-200 ${selectedPlan === currentPlan
              ? isDarkTheme
                ? "bg-gray-700 text-gray-400 cursor-not-allowed"
                : "bg-gray-100 text-gray-500 cursor-not-allowed"
              : "bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:shadow-lg hover:from-orange-500 hover:to-amber-500"
              }`}
          >
            {isProcessing ? "Processing..." : "Upgrade Plan"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpgradePage;